/**
 * Easy Yes/No that is not the budget package: weekly packages, waste ads /
 * keywords / ad groups that still leave something serving, search-term
 * excludes, and re-enable reminders for Core pauses. Google recs and
 * keyword-pause-waiting-on-search-terms sit on Needs a look. Campaign pause
 * stays off this list. Findings render in the shared Management table.
 */
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Check, Loader2, X } from 'lucide-react';
import { toast } from 'sonner';
import {
  useIsFetching,
  useMutation,
  useQuery,
  useQueryClient,
} from '@tanstack/react-query';

import { Badge } from '@/components/ui/badge';
import { TypeSetAsideQueue } from './TypeSetAsideQueue';
import { Button } from '@/components/ui/button';
import { useAuth } from '@/contexts/AuthContext';
import {
  applyApprovedRequest,
  listWeeklyApprovals,
  resolveApproval,
  reviewSearchTermsNegatives,
  type WeeklyApprovalRow,
} from '@/services/googleAdsWeeklyService';
import { operatorQueueCountsQueryKey } from '@/services/googleAdsOperatorQueueService';
import { AdsWaitState } from '@/components/google-ads/AdsWaitState';
import {
  ManagementTable,
  type ManagementColumn,
} from '@/components/google-ads/ManagementTable';
import {
  canWrite,
  setAdGroupStatus,
  setAdStatus,
  setKeywordStatus,
  type AdGroupRow,
  type AdMonitorResponse,
  type KeywordRow,
  type MonitoredAd,
  type StructureResponse,
} from '@/services/googleAdsManagementService';
import {
  PAUSE_WINDOW_DISCLAIMER,
  WEEKLY_FLOW_LABEL,
  countWeeklyItems,
  easySearchTermRecs,
  strategyEasyReadsOpen,
  easyWasteAdGroups,
  easyWasteAds,
  easyWasteKeywordsReady,
} from '@/components/google-ads/strategyBoardModel';
import {
  pauseWatchLookId,
  pruneReenabledWatch,
  readPauseWatch,
  removePauseWatch,
  snoozePauseWatch,
  upsertPauseWatch,
  visiblePauseWatch,
  writePauseWatch,
  type PauseWatchItem,
} from '@/components/google-ads/strategyPauseWatch';
const DISMISS_PREFIX = 'agileco.strategy-no.';

type EasySource =
  | 'Re-enable'
  | 'Weekly'
  | 'Pause ad'
  | 'Pause keyword'
  | 'Pause ad group'
  | 'Search term';

type EasyFinding = {
  id: string;
  source: EasySource;
  title: string;
  why: string;
  lookId: string;
  yesLabel: string;
  onYes?: () => void;
  onNo?: () => void;
  canAct: boolean;
  offLabel: string;
  flag?: string;
  flagLabel?: string;
};

const SOURCE_RANK: Record<EasySource, number> = {
  'Re-enable': 0,
  'Search term': 1,
  Weekly: 2,
  'Pause ad': 3,
  'Pause keyword': 4,
  'Pause ad group': 5,
};

function EasyDecideRow({
  yesLabel,
  onYes,
  onNo,
  canAct,
  offLabel,
  busy,
}: {
  yesLabel: string;
  onYes?: () => void;
  onNo?: () => void;
  canAct: boolean;
  offLabel: string;
  busy: boolean;
}) {
  // Bugs #6 and #7 of the 2026-09-15 rebuild spec: rows used to render
  // `No` even when Yes was gated off, so the operator could decline but
  // never accept. And when the row WAS off, the `offLabel` copy inside
  // the badge ("Writes off — open Keywords") got truncated by the fixed
  // column width — the operator saw "rights off open keywords".
  //
  // Now both buttons always render together. When the row is not
  // actionable, Yes is a disabled button carrying a stable short label
  // ("Not available") and the full reason moves to the tooltip. No
  // truncation because the label is fixed length; no asymmetric
  // affordance because both buttons are present.
  const yesEnabled = canAct && !!onYes && !busy;
  return (
    <div className="grid w-full min-w-0 grid-cols-2 gap-1">
      <Button
        type="button"
        size="sm"
        className="h-8 min-w-0 bg-[hsl(var(--status-success))] px-2 text-xs text-white hover:opacity-90 disabled:opacity-50"
        disabled={!yesEnabled}
        title={canAct ? undefined : offLabel}
        onClick={onYes}
      >
        {busy ? (
          <Loader2 className="mr-1 h-4 w-4 shrink-0 animate-spin" />
        ) : (
          <Check className="mr-1 h-4 w-4 shrink-0" />
        )}
        <span className="truncate">
          {canAct ? yesLabel : 'Not available'}
        </span>
      </Button>
      <Button
        type="button"
        size="sm"
        variant="outline"
        className="h-8 px-2 text-xs border-destructive/40 text-destructive hover:bg-destructive/10"
        disabled={!onNo || busy}
        onClick={onNo}
      >
        <X className="mr-1 h-4 w-4 shrink-0" />
        No
      </Button>
    </div>
  );
}

function readDismissed(tenantId: string): string[] {
  if (!tenantId) return [];
  try {
    const parsed = JSON.parse(
      window.localStorage.getItem(DISMISS_PREFIX + tenantId) || '[]',
    );
    return Array.isArray(parsed)
      ? parsed.filter((value): value is string => typeof value === 'string')
      : [];
  } catch {
    return [];
  }
}

function writeDismissed(tenantId: string, ids: string[]): void {
  try {
    window.localStorage.setItem(DISMISS_PREFIX + tenantId, JSON.stringify(ids));
  } catch {
    // Dismiss is convenience only.
  }
}

function writeErrorDetail(err: unknown): string {
  const detail = (err as { response?: { data?: { detail?: unknown } } })?.response
    ?.data?.detail;
  if (typeof detail === 'string') return detail;
  if (
    detail &&
    typeof detail === 'object' &&
    (detail as { code?: string }).code === 'blast_radius_confirmation_required'
  ) {
    return 'Too big for a one-click Yes. Open Search Terms and confirm there.';
  }
  if (
    detail &&
    typeof detail === 'object' &&
    typeof (detail as { message?: string }).message === 'string'
  ) {
    return (detail as { message: string }).message;
  }
  return String(err);
}

function formatPausedAt(iso: string): string {
  const ms = Date.parse(iso);
  if (Number.isNaN(ms)) return iso;
  return new Date(ms).toLocaleDateString();
}

export function StrategyEasyQueue({
  tenantId,
  ads,
  structure,
  adsLoading = false,
  structureLoading = false,
  adsError = false,
  structureError = false,
}: {
  tenantId: string;
  ads?: AdMonitorResponse;
  structure?: StructureResponse;
  adsLoading?: boolean;
  structureLoading?: boolean;
  adsError?: boolean;
  structureError?: boolean;
}) {
  const { user } = useAuth();
  const operatorEmail = user?.email ?? 'ui-operator';
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const canPauseAds = ads ? canWrite(ads.capabilities, 'ad_status_write') : false;
  const canPauseKeywords = structure
    ? canWrite(structure.capabilities, 'keyword_write')
    : false;
  const canPauseGroups = structure
    ? canWrite(structure.capabilities, 'ad_group_write')
    : false;

  const approvals = useQuery({
    queryKey: ['google-ads-weekly-approvals', tenantId],
    queryFn: () => listWeeklyApprovals(tenantId),
    enabled: Boolean(tenantId),
    refetchInterval: 15_000,
  });

  const searchTerms = useQuery({
    queryKey: ['google-ads-strategy-search-terms', tenantId],
    queryFn: () => reviewSearchTermsNegatives({ tenantId }),
    enabled: Boolean(tenantId),
    staleTime: 60_000,
  });

  useEffect(() => {
    if (!searchTerms.isSuccess) return;
    queryClient.invalidateQueries({
      queryKey: operatorQueueCountsQueryKey(tenantId),
    });
  }, [queryClient, searchTerms.dataUpdatedAt, searchTerms.isSuccess, tenantId]);

  const [awaitingStructure, setAwaitingStructure] = useState(false);
  const sawStructureFetch = useRef(false);
  const structureFetching = useIsFetching({ queryKey: ['ga-structure', tenantId] });

  useEffect(() => {
    if (structureFetching > 0) sawStructureFetch.current = true;
    if (awaitingStructure && sawStructureFetch.current && structureFetching === 0) {
      setAwaitingStructure(false);
      sawStructureFetch.current = false;
    }
  }, [awaitingStructure, structureFetching]);

  const [dismissed, setDismissed] = useState<string[]>(() =>
    readDismissed(tenantId),
  );
  const [pauseWatch, setPauseWatch] = useState<PauseWatchItem[]>(() =>
    readPauseWatch(tenantId),
  );

  useEffect(() => {
    setDismissed(readDismissed(tenantId));
    setPauseWatch(readPauseWatch(tenantId));
  }, [tenantId]);

  useEffect(() => {
    setPauseWatch((prev) => {
      const next = pruneReenabledWatch(prev, ads, structure);
      const same =
        next.length === prev.length &&
        next.every((item, index) => item.id === prev[index]?.id);
      if (same) return prev;
      writePauseWatch(tenantId, next);
      return next;
    });
  }, [ads, structure, tenantId]);

  const rememberPause = useCallback(
    (item: PauseWatchItem) => {
      setPauseWatch((prev) => {
        const next = upsertPauseWatch(prev, item);
        writePauseWatch(tenantId, next);
        return next;
      });
    },
    [tenantId],
  );

  const dismiss = useCallback((id: string) => {
    setDismissed((prev) => {
      const next = prev.includes(id) ? prev : [...prev, id];
      writeDismissed(tenantId, next);
      return next;
    });
    toast.success('No — left Ads unchanged');
  }, [tenantId]);

  const pending = (approvals.data ?? []).filter((row) => row.status === 'pending');
  const adsFailed = Boolean(ads && !ads.read_ok) || adsError;
  const structureFailed = Boolean(structure && !structure.read_ok) || structureError;
  const termsFailed = searchTerms.isError;
  const approvalsFailed = approvals.isError;
  const allTermRecs = searchTerms.isSuccess
    ? searchTerms.data.recommendations
    : undefined;
  const incompletes = [
    adsFailed ? ads?.message?.trim() || 'Could not read ads for this account' : null,
    structureFailed
      ? structure?.message?.trim() || 'Could not read structure for this account'
      : null,
    termsFailed ? 'Could not read search-term recommendations for this account' : null,
    approvalsFailed ? 'Could not read weekly pause/exclude packages' : null,
  ].filter((row): row is string => Boolean(row));
  const easyOpen =
    strategyEasyReadsOpen({
      adsFailed,
      structureFailed,
      searchTermsFailed: termsFailed,
    }) &&
    incompletes.length === 0 &&
    !adsLoading &&
    !structureLoading &&
    !searchTerms.isLoading;
  const termRecs = easySearchTermRecs(allTermRecs, easyOpen);
  const wasteAds = (
    easyOpen
      ? easyWasteAds(
          ads?.read_ok ? ads.ads : undefined,
          structure?.read_ok ? structure.ad_groups : undefined,
        )
      : []
  ).filter((ad) => !dismissed.includes(ad.resource_name || ad.ad_id));
  const wasteKeywords = (
    easyOpen && searchTerms.isSuccess
      ? easyWasteKeywordsReady(
          structure?.read_ok ? structure.keywords : undefined,
          allTermRecs,
        )
      : []
  ).filter((row) => !dismissed.includes(row.resource_name || row.criterion_id));
  const wasteGroups = (
    easyOpen
      ? easyWasteAdGroups(
          structure?.read_ok ? structure.ad_groups : undefined,
          {
            adsReadOk: Boolean(ads?.read_ok),
            darkAdGroupNames: ads?.read_ok ? ads.dark_ad_groups : undefined,
          },
        )
      : []
  ).filter((row) => !dismissed.includes(row.resource_name || row.ad_group_id));

  const decideWeekly = useMutation({
    mutationFn: async (vars: { row: WeeklyApprovalRow; action: 'approve' | 'reject' }) => {
      await resolveApproval({
        requestId: vars.row.id,
        action: vars.action,
        tenantId,
        resolvedBy: operatorEmail,
      });
      if (vars.action === 'approve') {
        return applyApprovedRequest(vars.row.id);
      }
      return null;
    },
    onSuccess: (applied, vars) => {
      if (vars.action === 'reject') {
        toast.success('No — left Ads unchanged');
      } else {
        toast.success(
          `Yes — applied ${applied?.applied ?? 0}. Save split was not updated.`,
        );
      }
      queryClient.invalidateQueries({
        queryKey: ['google-ads-weekly-approvals', tenantId],
      });
      queryClient.invalidateQueries({
        queryKey: operatorQueueCountsQueryKey(tenantId),
      });
      queryClient.invalidateQueries({ queryKey: ['ga-ad-monitor', tenantId] });
      queryClient.invalidateQueries({ queryKey: ['ga-structure', tenantId] });
    },
    onError: (err: unknown) => {
      toast.error(`Could not resolve: ${writeErrorDetail(err)}`);
    },
  });

  const pauseAd = useMutation({
    mutationFn: (ad: MonitoredAd) =>
      setAdStatus(tenantId, [ad.resource_name], 'PAUSED', 'ENABLED'),
    onSuccess: (result, ad) => {
      toast.success(
        `Paused ${result.changed} ad${result.changed === 1 ? '' : 's'} (${ad.label || ad.ad_id}). Review to re-enable later.`,
      );
      rememberPause({
        id: ad.resource_name || ad.ad_id,
        kind: 'ad',
        title: ad.label || ad.headline || ad.ad_id,
        pausedAt: new Date().toISOString(),
        lookId: pauseWatchLookId('ad'),
      });
      queryClient.invalidateQueries({ queryKey: ['ga-ad-monitor', tenantId] });
    },
    onError: (err: unknown) => {
      toast.error(`Pause failed: ${writeErrorDetail(err)}`);
    },
  });

  const pauseKeyword = useMutation({
    mutationFn: (row: KeywordRow) =>
      setKeywordStatus(tenantId, [row.resource_name], 'PAUSED'),
    onMutate: () => {
      sawStructureFetch.current = false;
      setAwaitingStructure(true);
    },
    onSuccess: (result, row) => {
      toast.success(
        `Paused ${result.changed} keyword (${row.text}). Review to re-enable later.`,
      );
      rememberPause({
        id: row.resource_name || row.criterion_id,
        kind: 'keyword',
        title: row.text,
        pausedAt: new Date().toISOString(),
        lookId: pauseWatchLookId('keyword'),
      });
      queryClient.invalidateQueries({ queryKey: ['ga-structure', tenantId] });
    },
    onError: (err: unknown) => {
      setAwaitingStructure(false);
      sawStructureFetch.current = false;
      toast.error(`Pause failed: ${writeErrorDetail(err)}`);
    },
  });

  const pauseGroup = useMutation({
    mutationFn: (row: AdGroupRow) =>
      setAdGroupStatus(tenantId, [row.resource_name], 'PAUSED'),
    onMutate: () => {
      sawStructureFetch.current = false;
      setAwaitingStructure(true);
    },
    onSuccess: (result, row) => {
      toast.success(
        `Paused ${result.changed} ad group (${row.name}). Review to re-enable later.`,
      );
      rememberPause({
        id: row.resource_name || row.ad_group_id,
        kind: 'ad_group',
        title: row.name,
        pausedAt: new Date().toISOString(),
        lookId: pauseWatchLookId('ad_group'),
      });
      queryClient.invalidateQueries({ queryKey: ['ga-structure', tenantId] });
    },
    onError: (err: unknown) => {
      setAwaitingStructure(false);
      sawStructureFetch.current = false;
      toast.error(`Pause failed: ${writeErrorDetail(err)}`);
    },
  });

  // CORE-M-03.d: `decideTerm` mutation retired. The negative-keyword
  // decisions live on the M-03 Negative Keyword Suggestions tab now;
  // this list links out instead of writing here. The applySearchTerm
  // service + toast copy stayed useful and moved to M-03.c already.

  const enableAd = useMutation({
    mutationFn: (item: PauseWatchItem) =>
      setAdStatus(tenantId, [item.id], 'ENABLED', 'PAUSED'),
    onSuccess: (result, item) => {
      toast.success(`Re-enabled ${result.changed} ad (${item.title}).`);
      setPauseWatch((prev) => {
        const next = removePauseWatch(prev, item.id);
        writePauseWatch(tenantId, next);
        return next;
      });
      queryClient.invalidateQueries({ queryKey: ['ga-ad-monitor', tenantId] });
    },
    onError: (err: unknown) => {
      toast.error(`Re-enable failed: ${writeErrorDetail(err)}`);
    },
  });

  const enableKeyword = useMutation({
    mutationFn: (item: PauseWatchItem) =>
      setKeywordStatus(tenantId, [item.id], 'ENABLED'),
    onMutate: () => {
      sawStructureFetch.current = false;
      setAwaitingStructure(true);
    },
    onSuccess: (result, item) => {
      toast.success(`Re-enabled ${result.changed} keyword (${item.title}).`);
      setPauseWatch((prev) => {
        const next = removePauseWatch(prev, item.id);
        writePauseWatch(tenantId, next);
        return next;
      });
      queryClient.invalidateQueries({ queryKey: ['ga-structure', tenantId] });
    },
    onError: (err: unknown) => {
      setAwaitingStructure(false);
      sawStructureFetch.current = false;
      toast.error(`Re-enable failed: ${writeErrorDetail(err)}`);
    },
  });

  const enableGroup = useMutation({
    mutationFn: (item: PauseWatchItem) =>
      setAdGroupStatus(tenantId, [item.id], 'ENABLED'),
    onMutate: () => {
      sawStructureFetch.current = false;
      setAwaitingStructure(true);
    },
    onSuccess: (result, item) => {
      toast.success(`Re-enabled ${result.changed} ad group (${item.title}).`);
      setPauseWatch((prev) => {
        const next = removePauseWatch(prev, item.id);
        writePauseWatch(tenantId, next);
        return next;
      });
      queryClient.invalidateQueries({ queryKey: ['ga-structure', tenantId] });
    },
    onError: (err: unknown) => {
      setAwaitingStructure(false);
      sawStructureFetch.current = false;
      toast.error(`Re-enable failed: ${writeErrorDetail(err)}`);
    },
  });

  const laterWatch = useCallback(
    (item: PauseWatchItem) => {
      setPauseWatch((prev) => {
        const next = snoozePauseWatch(prev, item.id);
        writePauseWatch(tenantId, next);
        return next;
      });
      toast.success('Reminder snoozed for 14 days');
    },
    [tenantId],
  );

  const busy =
    decideWeekly.isPending ||
    pauseAd.isPending ||
    pauseKeyword.isPending ||
    pauseGroup.isPending ||
    // CORE-M-03.d: decideTerm retired; the M-03 tab owns the negative-
    // keyword write path now, so this list no longer flips busy on it.
    enableAd.isPending ||
    enableKeyword.isPending ||
    enableGroup.isPending ||
    awaitingStructure;

  const findings = useMemo<EasyFinding[]>(() => {
    const rows: EasyFinding[] = [];
    for (const item of visiblePauseWatch(pauseWatch)) {
      const kindLabel =
        item.kind === 'ad'
          ? 'ad'
          : item.kind === 'keyword'
            ? 'keyword'
            : 'ad group';
      const onYes =
        item.kind === 'ad'
          ? () => enableAd.mutate(item)
          : item.kind === 'keyword'
            ? () => enableKeyword.mutate(item)
            : () => enableGroup.mutate(item);
      const canAct =
        item.kind === 'ad'
          ? canPauseAds
          : item.kind === 'keyword'
            ? canPauseKeywords
            : canPauseGroups;
      rows.push({
        id: `reenable-${item.id}`,
        source: 'Re-enable',
        title: `Re-enable ${kindLabel} · ${item.title}`,
        why: `Core paused this on ${formatPausedAt(item.pausedAt)}. ${PAUSE_WINDOW_DISCLAIMER}`,
        lookId: item.lookId,
        yesLabel: 'Yes · enable',
        onYes,
        onNo: () => laterWatch(item),
        canAct,
        offLabel: 'Writes off — open Management',
        flagLabel: 'Re-enable',
        flag: 'People forget paused keywords and ad groups. Yes turns it back on. No snoozes this reminder.',
      });
    }
    // CORE-M-03.d: negative-keyword suggestions moved to the M-03 tab
    // (per-row Yes/No + EvidenceRow). Prior to this change the same rows
    // rendered here too — same endpoint, different React Query key — so
    // an operator saw the same suggestion in two places and Yes on one
    // did not clear the other until the backend refetch caught up. Now
    // this list renders ONE summary row that links out to M-03.
    if (termRecs.length > 0) {
      rows.push({
        id: 'search-term-summary',
        source: 'Search term',
        title: `${termRecs.length} negative-keyword suggestion${
          termRecs.length === 1 ? '' : 's'
        } waiting`,
        why: 'Each suggestion is one search term Core wants to add as a shared negative. The full list and per-row Yes/No live on Negative Keyword Suggestions in Management — open it to decide.',
        lookId: 'keywords',
        yesLabel: 'Open Negative Keyword Suggestions →',
        onYes: () =>
          navigate('/ads-tools/management?platform=google-ads&tab=negatives'),
        onNo: undefined,
        canAct: true,
        offLabel: 'Open Negative Keyword Suggestions',
      });
    }
    for (const row of easyOpen ? pending : []) {
      rows.push({
        id: row.id,
        source: 'Weekly',
        title: WEEKLY_FLOW_LABEL[row.flow] ?? row.flow,
        why: `${countWeeklyItems(row)} item${countWeeklyItems(row) === 1 ? '' : 's'} in one package. Yes applies the package.`,
        lookId: 'ad-health',
        yesLabel: 'Yes',
        onYes: () => decideWeekly.mutate({ row, action: 'approve' }),
        onNo: () => decideWeekly.mutate({ row, action: 'reject' }),
        canAct: true,
        offLabel: 'Writes off',
      });
    }
    for (const ad of wasteAds) {
      rows.push({
        id: ad.resource_name || ad.ad_id,
        source: 'Pause ad',
        title: `Pause ad · ${ad.label || ad.headline || ad.ad_id}`,
        why: `${ad.campaign_name} / ${ad.ad_group_name}. Spent and converted nothing. Another ad stays live in the group.`,
        lookId: 'ad-health',
        yesLabel: 'Yes · pause',
        onYes: () => pauseAd.mutate(ad),
        onNo: () => dismiss(ad.resource_name || ad.ad_id),
        canAct: canPauseAds,
        offLabel: 'Writes off — open Ad health',
        flagLabel: 'Window only',
        flag: PAUSE_WINDOW_DISCLAIMER,
      });
    }
    for (const row of wasteKeywords) {
      rows.push({
        id: row.resource_name || row.criterion_id,
        source: 'Pause keyword',
        title: `Pause keyword · ${row.text}`,
        why: `${row.campaign_name} / ${row.ad_group_name} · ${row.match_type}. Spent and converted nothing. A non-waste keyword stays live in the group.`,
        lookId: 'keywords',
        yesLabel: 'Yes · pause',
        onYes: () => pauseKeyword.mutate(row),
        onNo: () => dismiss(row.resource_name || row.criterion_id),
        canAct: canPauseKeywords,
        offLabel: 'Writes off — open Keywords',
        flagLabel: 'Window only',
        flag: PAUSE_WINDOW_DISCLAIMER,
      });
    }
    for (const row of wasteGroups) {
      rows.push({
        id: row.resource_name || row.ad_group_id,
        source: 'Pause ad group',
        title: `Pause ad group · ${row.name}`,
        why: `${row.campaign_name}. Spent and converted nothing. Another ENABLED group still served in the window and is not dark.`,
        lookId: 'ad-groups',
        yesLabel: 'Yes · pause',
        onYes: () => pauseGroup.mutate(row),
        onNo: () => dismiss(row.resource_name || row.ad_group_id),
        canAct: canPauseGroups,
        offLabel: 'Writes off — open Ad groups',
        flagLabel: 'Window only',
        flag: PAUSE_WINDOW_DISCLAIMER,
      });
    }
    return rows;
  }, [
    canPauseAds,
    canPauseGroups,
    canPauseKeywords,
    // CORE-M-03.d: decideTerm retired (see comment above the removed
    // mutation); navigate is the new dep for the link-out row.
    decideWeekly,
    dismiss,
    enableAd,
    enableGroup,
    enableKeyword,
    laterWatch,
    navigate,
    pauseAd,
    pauseGroup,
    pauseKeyword,
    pauseWatch,
    pending,
    termRecs,
    wasteAds,
    wasteGroups,
    wasteKeywords,
  ]);

  const columns = useMemo<ManagementColumn<EasyFinding>[]>(
    () => [
      {
        key: 'finding',
        header: 'Finding',
        width: 420,
        fill: true,
        cellClassName: 'align-top',
        sort: (row) => row.title.toLowerCase(),
        filter: (row) => `${row.title} ${row.why}`,
        render: (row) => (
          <div>
            <div className="font-medium text-foreground">{row.title}</div>
            <div className="text-xs text-muted-foreground">{row.why}</div>
            {row.flag ? (
              <div className="mt-1.5 flex flex-wrap items-start gap-1.5">
                <Badge variant="outline" className="font-normal">
                  {row.flagLabel || 'Window only'}
                </Badge>
                <span className="text-[11px] text-muted-foreground">{row.flag}</span>
              </div>
            ) : null}
          </div>
        ),
      },
      {
        key: 'source',
        header: 'Source',
        width: 140,
        cellClassName: 'align-top',
        sort: (row) => SOURCE_RANK[row.source],
        filter: (row) => row.source,
        render: (row) => row.source,
      },
      {
        key: 'decide',
        header: 'Decide',
        width: 360,
        cellClassName: 'align-middle',
        render: (row) => (
          <EasyDecideRow
            yesLabel={row.yesLabel}
            onYes={row.onYes}
            onNo={row.onNo}
            canAct={row.canAct}
            offLabel={row.offLabel}
            busy={busy}
          />
        ),
      },
    ],
    [busy],
  );

  const hasRows = findings.length > 0;

  // ⚠ Do not require every source to still be loading. Weekly and
  // search-terms often finish first; ads/structure take one to two minutes.
  // The old AND-gate then painted "no rows to review" while Google was still
  // answering — the list that used to fill itself looked empty.
  const fillsStillLoading =
    approvals.isLoading ||
    searchTerms.isLoading ||
    adsLoading ||
    structureLoading;

  if (!hasRows && fillsStillLoading) {
    return (
      <AdsWaitState
        compact
        className="px-4"
        stage="Checking findings"
      />
    );
  }

  if (!hasRows) {
    if (incompletes.length > 0) {
      return (
        <div className="space-y-2 px-4 py-3">
          {incompletes.map((message) => (
            <div key={message} className="border-coral/40 bg-coral-light/20 p-3">
              <p className="text-sm font-medium text-coral-dark">{message}</p>
              <p className="mt-1 text-xs text-muted-foreground">
                This list is incomplete, not empty.
              </p>
            </div>
          ))}
        </div>
      );
    }
    return (
      <div className="space-y-2 px-4 py-3">
        <p className="text-sm text-muted-foreground">
          No findings to review. Review strategy for budget moves, or run
          Weekly in soft_auto to fill this list.
        </p>
      </div>
    );
  }

  return (
    <div className="space-y-3 px-1 pb-3">
      {awaitingStructure ? (
        <p className="px-3 py-2 text-sm text-muted-foreground">
          <Loader2 className="mr-2 inline h-4 w-4 animate-spin" />
          Refreshing structure… Yes stays off until the live read settles.
        </p>
      ) : null}
      {incompletes.map((message) => (
        <div
          key={message}
          className="border border-coral/40 bg-coral-light/20 px-4 py-3"
        >
          <p className="text-sm font-medium text-coral-dark">{message}</p>
          <p className="mt-1 text-xs text-muted-foreground">
            This list is incomplete, not empty.
          </p>
        </div>
      ))}
      <TypeSetAsideQueue tenantId={tenantId} operatorEmail={operatorEmail} />
      <ManagementTable<EasyFinding>
        rows={findings}
        columns={columns}
        getRowId={(row) => row.id}
        searchFields={(row) => [row.title, row.why, row.source]}
        searchPlaceholder="Search findings…"
        filters={[
          { key: 'source', label: 'Source', value: (row) => row.source },
        ]}
        defaultSortKey="source"
        defaultSortDir="asc"
        resizable={false}
        emptyText="No findings to review."
        noMatchText="Nothing matches this search or filter."
      />
    </div>
  );
}
